// imports
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Alert, Typography, useMediaQuery } from '@mui/material';

// local imports
import ReusableMenu from '../ReusableMenu';
import { months, monthsAbbr } from '../../../../constants/Months';
import StyledSwitch from '../../StyledSwitch';
import { useGetAwdConfigQuery, selectModelById } from '../../../../services/awdConfigApi';
import { getUrlParam } from '../../../../utils/getUrlParam';
import { getAvailableMonths } from '../../../../utils/getAvailableMonths';

// extract month and year separately from startDate and endDate
const getYears = (startDate: string | null, endDate: string) => {
  const startYear = startDate
    ? parseInt(startDate.split('-')[0])
    : new Date().getFullYear();
  const endYear = parseInt(endDate.split('-')[0]);
  const years = [];

  // Always create a range that includes both years, regardless of order
  const minYear = Math.min(startYear, endYear);
  const maxYear = Math.max(startYear, endYear);

  for (let year = minYear; year <= maxYear; year++) {
    years.push({ key: year.toString(), value: year.toString() });
  }
  return years;
};

// startDate and endDate expected format is "YYYY-MM"
interface DateMenuProps {
  selectedModel: string;
  setStartMonth: (month: string | null) => void;
  setStartYear: (year: string | null) => void;
  setEndMonth: (month: string | null) => void;
  setEndYear: (year: string | null) => void;
  isMonthly: boolean;
  initialUseDateRange?: boolean;
  onAlertVisibilityChange?: (isVisible: boolean) => void;
  onUseDateRangeChange?: (useDateRange: boolean) => void;
}

type YearOption = { key: string; value: string };

export default function DateMenu({
  selectedModel,
  setStartMonth,
  setStartYear,
  setEndMonth,
  setEndYear,
  isMonthly,
  initialUseDateRange = false,
  onAlertVisibilityChange,
  onUseDateRangeChange,
}: DateMenuProps) {
  const isSmallScreen = useMediaQuery('(max-width:650px)');

  // Use abbreviated months on small screens, full months otherwise
  const displayMonths = isSmallScreen ? monthsAbbr : months;

  // Adjust bar margin based on screen size
  const barMargin = isSmallScreen ? '0 0px' : undefined;

  // get date range from AWD config for the selected variable's model
  const { data: awdConfig, isSuccess } = useGetAwdConfigQuery();

  const modelDateRange = useMemo(() => {
    if (!awdConfig) return null;
    const model = selectModelById(awdConfig.models, selectedModel);
    if (!model) return null;
    return {
      start_date: `${model.startYear}-${model.startMonth}`,
      end_date: `${model.endYear}-${model.endMonth}`,
      startYear: model.startYear,
      startMonth: model.startMonth,
      endYear: model.endYear,
      endMonth: model.endMonth,
    };
  }, [awdConfig, selectedModel]);

  // Initialize useDateRange from URL if available, otherwise use initialUseDateRange
  const [useDateRange, setUseDateRange] = useState(() => {
    const urlUseDateRange = getUrlParam('useDateRange');
    return urlUseDateRange ? urlUseDateRange === 'true' : initialUseDateRange;
  });

  // Track month selections by key (e.g., '01' for January) rather than index
  const [selectedStartMonthKey, setSelectedStartMonthKey] = useState('01');
  const [selectedStartYearIndex, setSelectedStartYearIndex] = useState(0);
  const [selectedEndMonthKey, setSelectedEndMonthKey] = useState('01');
  const [selectedEndYearIndex, setSelectedEndYearIndex] = useState(0);
  const [yearOptions, setYearOptions] = useState<YearOption[]>([]);
  const [showDateAlert, setShowDateAlert] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const showMonth = isMonthly;

  // Compute filtered month options based on selected year and model boundaries
  const availableStartMonths = useMemo(() => {
    if (!modelDateRange || !yearOptions.length) return displayMonths;
    const selectedYear = yearOptions[selectedStartYearIndex]?.key;
    if (!selectedYear) return displayMonths;
    return getAvailableMonths(
      displayMonths,
      selectedYear,
      modelDateRange.startYear,
      modelDateRange.startMonth,
      modelDateRange.endYear,
      modelDateRange.endMonth
    );
  }, [displayMonths, modelDateRange, yearOptions, selectedStartYearIndex]);

  const availableEndMonths = useMemo(() => {
    if (!modelDateRange || !yearOptions.length) return displayMonths;
    const selectedYear = yearOptions[selectedEndYearIndex]?.key;
    if (!selectedYear) return displayMonths;
    return getAvailableMonths(
      displayMonths,
      selectedYear,
      modelDateRange.startYear,
      modelDateRange.startMonth,
      modelDateRange.endYear,
      modelDateRange.endMonth
    );
  }, [displayMonths, modelDateRange, yearOptions, selectedEndYearIndex]);

  // Derive display indices from month keys
  const startMonthDisplayIndex = useMemo(() => {
    const index = availableStartMonths.findIndex((m) => m.key === selectedStartMonthKey);
    return index >= 0 ? index : 0;
  }, [availableStartMonths, selectedStartMonthKey]);

  const endMonthDisplayIndex = useMemo(() => {
    const index = availableEndMonths.findIndex((m) => m.key === selectedEndMonthKey);
    return index >= 0 ? index : 0;
  }, [availableEndMonths, selectedEndMonthKey]);

  // Notify parent when alert visibility changes
  useEffect(() => {
    onAlertVisibilityChange?.(showDateAlert && useDateRange);
  }, [showDateAlert, useDateRange, onAlertVisibilityChange]);

  const checkDateValidity = useCallback(
    (
      startMonthKey: string,
      startYearIndex: number,
      endMonthKey: string,
      endYearIndex: number,
      years: { key: string; value: string }[]
    ) => {
      if (
        !years.length ||
        startYearIndex < 0 ||
        endYearIndex < 0 ||
        startYearIndex >= years.length ||
        endYearIndex >= years.length
      ) {
        return;
      }

      const startYear = years[startYearIndex]?.key;
      const endYear = years[endYearIndex]?.key;

      if (endYear && startYear) {
        if (parseInt(endYear) < parseInt(startYear)) {
          setShowDateAlert(true);
          return;
        } else if (parseInt(endYear) === parseInt(startYear)) {
          if (endMonthKey <= startMonthKey) {
            setShowDateAlert(true);
            return;
          }
        }
      }

      setShowDateAlert(false);
    },
    []
  );

  useEffect(() => {
    // Don't initialize if we've already initialized or don't have required data
    if (hasInitialized || !isSuccess || !modelDateRange) {
      return;
    }

    // set years
    const years = getYears(modelDateRange.start_date, modelDateRange.end_date);
    // Initializing yearOptions from API data inside a guarded effect (hasInitialized check
    // prevents repeated calls). This is a one-time data-driven initialization, not a
    // reactive loop, so the cascading-render concern does not apply.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setYearOptions(years);

    let startMonthKey = '01';
    let startYearIndex = 0;
    let endMonthKey = '01';
    let endYearIndex = 0;

    // Check for URL parameters first, then fall back to props
    const urlStartYear = getUrlParam('startYear');
    const urlStartMonth = getUrlParam('startMonth');
    const urlEndYear = getUrlParam('endYear');
    const urlEndMonth = getUrlParam('endMonth');

    // Handle start date - prefer URL, fallback to startDate prop
    if (urlStartYear && years.some((year) => year.key === urlStartYear)) {
      startYearIndex = years.findIndex((year) => year.key === urlStartYear);
      if (urlStartMonth && isMonthly) {
        const monthExists = months.some((month) => month.key === urlStartMonth);
        if (monthExists) {
          startMonthKey = urlStartMonth;
        }
      }
    } else if (modelDateRange.start_date) {
      startYearIndex = years.length - 1;
      startMonthKey = '01';
    }

    // Handle end date - prefer URL, fallback to endDate prop
    if (
      urlEndYear &&
      years.some((year) => year.key === urlEndYear) &&
      useDateRange
    ) {
      endYearIndex = years.findIndex((year) => year.key === urlEndYear);
      if (urlEndMonth && isMonthly) {
        const monthExists = months.some((month) => month.key === urlEndMonth);
        if (monthExists) {
          endMonthKey = urlEndMonth;
        }
      }
    } else if (modelDateRange.end_date) {
      const [year, month] = modelDateRange.end_date.split('-');
      endMonthKey = month;
      endYearIndex = years.findIndex((y) => y.key === parseInt(year).toString());
    }

    // Validate month keys against available months for the selected years
    const startYearValue = years[startYearIndex]?.key;
    if (startYearValue) {
      const availableStart = getAvailableMonths(
        months,
        startYearValue,
        modelDateRange.startYear,
        modelDateRange.startMonth,
        modelDateRange.endYear,
        modelDateRange.endMonth
      );
      if (!availableStart.some((m) => m.key === startMonthKey)) {
        startMonthKey = availableStart[0]?.key ?? '01';
      }
    }

    const endYearValue = years[endYearIndex]?.key;
    if (endYearValue) {
      const availableEnd = getAvailableMonths(
        months,
        endYearValue,
        modelDateRange.startYear,
        modelDateRange.startMonth,
        modelDateRange.endYear,
        modelDateRange.endMonth
      );
      if (!availableEnd.some((m) => m.key === endMonthKey)) {
        endMonthKey = availableEnd[0]?.key ?? '01';
      }
    }

    // Set the state
    setSelectedStartMonthKey(startMonthKey);
    setSelectedStartYearIndex(startYearIndex);
    setSelectedEndMonthKey(endMonthKey);
    setSelectedEndYearIndex(endYearIndex);

    // Update local state only - URL params updated when Update Map is clicked
    if (years[startYearIndex]) {
      setStartYear(years[startYearIndex].value);
      if (isMonthly) {
        const monthValue = months.find((m) => m.key === startMonthKey)?.value ?? null;
        setStartMonth(monthValue);
      }
    }

    if (useDateRange && years[endYearIndex]) {
      setEndYear(years[endYearIndex].value);
      if (isMonthly) {
        const monthValue = months.find((m) => m.key === endMonthKey)?.value ?? null;
        setEndMonth(monthValue);
      }
    }

    // Only run end date validation if useDateRange is currently true
    if (useDateRange) {
      checkDateValidity(
        startMonthKey,
        startYearIndex,
        endMonthKey,
        endYearIndex,
        years
      );
    }

    setHasInitialized(true);
  }, [
    modelDateRange,
    isMonthly,
    checkDateValidity,
    useDateRange,
    hasInitialized,
    isSuccess,
    setEndMonth,
    setEndYear,
    setStartMonth,
    setStartYear,
  ]);

  // Reset initialization when key props change
  useEffect(() => {
    // Resetting a boolean flag so the initialization effect re-runs when the
    // variable's date range changes. This is a deliberate, single-state update
    // that triggers one controlled re-initialization cycle.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasInitialized(false);
  }, [modelDateRange?.start_date, modelDateRange?.end_date]);

  useEffect(() => {
    if (useDateRange && yearOptions.length > 0) {
      // checkDateValidity conditionally calls setShowDateAlert based on derived
      // comparison logic. It only sets state when the validity result changes and
      // is guarded by the useDateRange + yearOptions conditions above.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      checkDateValidity(
        selectedStartMonthKey,
        selectedStartYearIndex,
        selectedEndMonthKey,
        selectedEndYearIndex,
        yearOptions
      );
    }
  }, [
    selectedStartMonthKey,
    selectedStartYearIndex,
    selectedEndMonthKey,
    selectedEndYearIndex,
    useDateRange,
    yearOptions,
    checkDateValidity,
  ]);

  // Notify parent when useDateRange changes
  useEffect(() => {
    onUseDateRangeChange?.(useDateRange);
  }, [useDateRange, onUseDateRangeChange]);

  const handleSelectStartMonth = (index: number) => {
    const monthKey = availableStartMonths[index]?.key;
    if (monthKey) {
      setSelectedStartMonthKey(monthKey);
      if (isMonthly) {
        const monthValue = months.find((m) => m.key === monthKey)?.value ?? null;
        setStartMonth(monthValue);
      }
    }
  };

  const handleSelectStartYear = (index: number) => {
    setSelectedStartYearIndex(index);
    if (yearOptions[index]) {
      setStartYear(yearOptions[index].value);
    }

    // Check if current month is still valid for the new year
    if (modelDateRange && yearOptions[index]) {
      const newAvailable = getAvailableMonths(
        months,
        yearOptions[index].key,
        modelDateRange.startYear,
        modelDateRange.startMonth,
        modelDateRange.endYear,
        modelDateRange.endMonth
      );
      if (!newAvailable.some((m) => m.key === selectedStartMonthKey)) {
        const newMonthKey = newAvailable[0]?.key ?? '01';
        setSelectedStartMonthKey(newMonthKey);
        if (isMonthly) {
          const monthValue = months.find((m) => m.key === newMonthKey)?.value ?? null;
          setStartMonth(monthValue);
        }
      }
    }
  };

  const handleSelectEndMonth = (index: number) => {
    const monthKey = availableEndMonths[index]?.key;
    if (monthKey) {
      setSelectedEndMonthKey(monthKey);
      if (isMonthly && useDateRange) {
        const monthValue = months.find((m) => m.key === monthKey)?.value ?? null;
        setEndMonth(monthValue);
      }
    }
  };

  const handleSelectEndYear = (index: number) => {
    setSelectedEndYearIndex(index);
    if (useDateRange && yearOptions[index]) {
      setEndYear(yearOptions[index].value);
    }

    // Check if current month is still valid for the new year
    if (modelDateRange && yearOptions[index]) {
      const newAvailable = getAvailableMonths(
        months,
        yearOptions[index].key,
        modelDateRange.startYear,
        modelDateRange.startMonth,
        modelDateRange.endYear,
        modelDateRange.endMonth
      );
      if (!newAvailable.some((m) => m.key === selectedEndMonthKey)) {
        const newMonthKey = newAvailable[0]?.key ?? '01';
        setSelectedEndMonthKey(newMonthKey);
        if (isMonthly && useDateRange) {
          const monthValue = months.find((m) => m.key === newMonthKey)?.value ?? null;
          setEndMonth(monthValue);
        }
      }
    }
  };

  const gridSize = showMonth ? 6 : 12;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'left',
        boxSizing: 'border-box',
      }}
    >
      <Grid container spacing={1}>
        {showMonth && (
          <Grid size={gridSize} data-testid="start-month-menu-container">
            <ReusableMenu
              title="Month"
              options={availableStartMonths}
              selectedIndex={startMonthDisplayIndex}
              onSelect={handleSelectStartMonth}
              barMargin={barMargin}
            />
          </Grid>
        )}
        <Grid size={gridSize} data-testid="start-year-menu-container">
          <ReusableMenu
            title="Year"
            options={yearOptions}
            selectedIndex={selectedStartYearIndex}
            onSelect={handleSelectStartYear}
            barMargin={barMargin}
          />
        </Grid>
      </Grid>
      {/* TODO: Delete the import.meta.env.DEV condition once time series is functional. */}
      {/* For now, only show the time series section in Dev. */}
      {import.meta.env.DEV && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 0 16px',
            }}
          >
            <Typography
              data-testid="additional-layer-name"
              component="span"
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color: '#000000',
                fontSize: '1.125rem',
              }}
            >
              Create a Time Series
            </Typography>
            <StyledSwitch
              sx={{ marginRight: '0px' }}
              edge="end"
              onClick={() => {
                setUseDateRange((prev) => {
                  const newValue = !prev;
                  if (!newValue) {
                    setShowDateAlert(false);
                  }
                  return newValue;
                });
              }}
              checked={useDateRange}
              slotProps={{
                input: { id: 'toggle-time-series', 'aria-label': 'Create a time series', role: 'switch' },
              }}
              data-testid="end-date-button"
            />
          </Box>
          {useDateRange && (
            <Grid container spacing={1} sx={{ pb: '6px' }}>
              {showMonth && (
                <Grid size={gridSize} data-testid="end-month-menu-container">
                  <ReusableMenu
                    title="End Month"
                    options={availableEndMonths}
                    selectedIndex={endMonthDisplayIndex}
                    onSelect={handleSelectEndMonth}
                    barMargin={barMargin}
                  />
                </Grid>
              )}
              <Grid size={gridSize} data-testid="end-year-menu-container">
                <ReusableMenu
                  title="End Year"
                  options={yearOptions}
                  selectedIndex={selectedEndYearIndex}
                  onSelect={handleSelectEndYear}
                  barMargin={barMargin}
                />
              </Grid>
            </Grid>
          )}
          {showDateAlert && useDateRange && (
            <Alert severity="error" sx={{ marginBottom: '10px' }}>
              End date must be later than start date.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
